export type GmailIntegrationErrorKind =
  | 'unauthorized'
  | 'offline'
  | 'expired'
  | 'conflict'
  | 'no_family'
  | 'invalid'
  | 'server'
  | 'generic';

interface IGmailClassifiableError {
  gmailIntegrationKind?: GmailIntegrationErrorKind;
  code?: unknown;
  message?: unknown;
  request?: unknown;
  response?: {
    status?: unknown;
    data?: {
      code?: unknown;
      errorCode?: unknown;
      error?: { code?: unknown };
    };
  };
}

export class GmailIntegrationClientError extends Error {
  public readonly gmailIntegrationKind: GmailIntegrationErrorKind;

  constructor(kind: GmailIntegrationErrorKind) {
    super('Gmail integration request could not be completed');
    this.name = 'GmailIntegrationClientError';
    this.gmailIntegrationKind = kind;
  }
}

function normalizedCode(error: IGmailClassifiableError): string {
  const data = error.response && error.response.data;
  const value = data && (data.code || data.errorCode || (data.error && data.error.code));
  return typeof value === 'string' ? value.toUpperCase().replace(/[-\s]+/g, '_') : '';
}

export function classifyGmailIntegrationError(error: unknown): GmailIntegrationErrorKind {
  const candidate = (error || {}) as IGmailClassifiableError;

  if (candidate.gmailIntegrationKind) {
    return candidate.gmailIntegrationKind;
  }

  const code = normalizedCode(candidate);
  if (code.indexOf('SESSION_EXPIRED') !== -1 || code.indexOf('UNAUTHORIZED') !== -1) {
    return 'unauthorized';
  }
  if (code.indexOf('EXPIRED') !== -1 || code.indexOf('USED') !== -1) {
    return 'expired';
  }
  if (code.indexOf('ALREADY_CONNECTED') !== -1 || code.indexOf('CONFLICT') !== -1) {
    return 'conflict';
  }
  if (
    code.indexOf('NO_AUTHORIZED_FAMIL') !== -1 ||
    code.indexOf('NO_FAMIL') !== -1 ||
    code.indexOf('NO_TARGET') !== -1
  ) {
    return 'no_family';
  }
  if (code.indexOf('INVALID') !== -1) {
    return 'invalid';
  }

  const status = candidate.response && candidate.response.status;
  if (status === 401 || status === 403) {
    return 'unauthorized';
  }
  if (status === 409) {
    return 'conflict';
  }
  if (status === 410) {
    return 'expired';
  }
  if (status === 400 || status === 404 || status === 422) {
    return 'invalid';
  }
  if (typeof status === 'number' && status >= 500) {
    return 'server';
  }

  const navigatorIsOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const transportCode = typeof candidate.code === 'string' ? candidate.code.toUpperCase() : '';
  const transportMessage = typeof candidate.message === 'string' ? candidate.message.toLowerCase() : '';
  if (
    !candidate.response &&
    (
      navigatorIsOffline ||
      Boolean(candidate.request) ||
      transportCode === 'ECONNABORTED' ||
      transportCode === 'ERR_NETWORK' ||
      transportMessage.indexOf('network error') !== -1 ||
      transportMessage.indexOf('timeout') !== -1
    )
  ) {
    return 'offline';
  }

  return 'generic';
}



// WEBPACK FOOTER //
// ./src/services/gmailIntegration/gmailIntegration.errors.ts